import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { pickHighestContrast } from '../utils/luminance';

// ============================================================================
// Content schema (self-contained literal — statically extractable at preview
// time). The slot group definitions used by the hooks are derived from it
// below so there is a single source of truth.
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {
    'residentaccess.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Resident Access',
    },
  },
  slotGroups: {
    'residentaccess.payrent_link': {
      label: 'Pay Rent Link',
      fields: {
        label: { label: 'Label', type: 'text', defaultValue: 'Pay Rent' },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url', 'property_online_leasing_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'residentaccess.payrent_image': {
      label: 'Pay Rent Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select one bright, well-lit kitchen or dining-area photo — ideally a standout shot of the kitchen. Avoid blurry, dark, or empty-lot photos and floor-plan diagrams.',
        context:
          'One of three "resident access" action cards (Pay Rent, Resident Portal, Service Request). Each card must use a different photo.',
        sourceCollection: 'photo_gallery',
      },
    },
    'residentaccess.portal_link': {
      label: 'Resident Portal Link',
      fields: {
        label: { label: 'Label', type: 'text', defaultValue: 'Resident Portal' },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'residentaccess.portal_image': {
      label: 'Resident Portal Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 1,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select one bright, well-lit bedroom photo. Avoid laundry or utility rooms (washer/dryer), blurry or dark shots, empty-lot photos, and floor-plan diagrams.',
        context:
          'The centre, highlighted card of three "resident access" action cards (Pay Rent, Resident Portal, Service Request). Each card must use a different photo.',
        sourceCollection: 'photo_gallery',
      },
    },
    'residentaccess.service_link': {
      label: 'Service Request Link',
      fields: {
        label: { label: 'Label', type: 'text', defaultValue: 'Service Request' },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'residentaccess.service_image': {
      label: 'Service Request Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 2,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select one bright, well-lit living room or general living-space photo. Avoid bathrooms, laundry or utility rooms, blurry or dark shots, empty-lot photos, and floor-plan diagrams.',
        context:
          'One of three "resident access" action cards (Pay Rent, Resident Portal, Service Request). Each card must use a different photo.',
        sourceCollection: 'photo_gallery',
      },
    },
  },
};

const groups = contentSchema.slotGroups as Record<string, SlotGroupDefinition>;
const payRentLinkDef = groups['residentaccess.payrent_link'];
const payRentImageDef = groups['residentaccess.payrent_image'];
const portalLinkDef = groups['residentaccess.portal_link'];
const portalImageDef = groups['residentaccess.portal_image'];
const serviceLinkDef = groups['residentaccess.service_link'];
const serviceImageDef = groups['residentaccess.service_image'];

// ============================================================================
// Render helpers
// ============================================================================

type SlotValues = Record<string, string | null>;

const CardImage = ({ image, label }: { image: SlotValues; label: string }) => {
  const imgSrc = image.image || '';
  return (
    <div className="ra-card-media">
      {imgSrc ? (
        <VibesitesImage
          url={imgSrc}
          alt={image.alt || label}
          className="ra-card-img"
          sizes="(min-width: 768px) 33vw, 100vw"
        />
      ) : (
        <div className="ra-card-img ra-card-img--placeholder" aria-hidden="true" />
      )}
    </div>
  );
};

/**
 * The colored label bar. When the link has a URL it renders as an anchor whose
 * `::after` (`.ra-card-stretch`) stretches over the whole card, so the entire
 * card — image included — is clickable without nesting the image slot group
 * inside the anchor.
 */
const CardBar = ({
  link,
  label,
  bg,
  color,
}: {
  link: SlotValues;
  label: string;
  bg: string;
  color: string;
}) => {
  const inner: ReactNode = <span className="ra-card-label">{label}</span>;
  const style = { background: bg, color };
  if (!link.url) {
    return (
      <div className="ra-card-bar" style={style}>
        {inner}
      </div>
    );
  }
  return (
    <a
      href={link.url}
      target={link._target || '_blank'}
      rel={link._target === '_self' ? undefined : 'noopener noreferrer'}
      className="ra-card-bar ra-card-stretch"
      style={style}
    >
      {inner}
    </a>
  );
};

// ============================================================================
// Component
// ============================================================================

export function ResidentAccess() {
  const payRentLink = useSlotGroup('residentaccess.payrent_link', payRentLinkDef);
  const payRentImage = useSlotGroup('residentaccess.payrent_image', payRentImageDef);
  const portalLink = useSlotGroup('residentaccess.portal_link', portalLinkDef);
  const portalImage = useSlotGroup('residentaccess.portal_image', portalImageDef);
  const serviceLink = useSlotGroup('residentaccess.service_link', serviceLinkDef);
  const serviceImage = useSlotGroup('residentaccess.service_image', serviceImageDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  // Bars sit on theme colors that a property can override, so resolve legible
  // label text against the actual resolved token rather than hard-coding it.
  const tokens = useTokens();
  const { standardBg, standardText, highlightBg, highlightText } = useMemo(() => {
    const c = tokens?.color;
    const sBg = c?.secondary || '#3E441D';
    const hBg = c?.primary || '#B28C59';
    const dark = c?.text || '#2A2A2A';
    return {
      standardBg: 'var(--vibe-color-secondary)',
      standardText:
        pickHighestContrast(sBg, [c?.textOnSecondary || '#FFFFFF', dark]) ?? '#FFFFFF',
      highlightBg: 'var(--vibe-color-primary)',
      highlightText:
        pickHighestContrast(hBg, [c?.textOnPrimary || '#FFFFFF', dark]) ?? '#FFFFFF',
    };
  }, [tokens]);

  const payRentLabel = payRentLink.label || 'Pay Rent';
  const portalLabel = portalLink.label || 'Resident Portal';
  const serviceLabel = serviceLink.label || 'Service Request';

  return (
    <section className="ra-section">
      <div ref={sectionRef} className="reveal-fade-up ra-container">
        <EditableToken path="residentaccess.heading" as="h2" className="sr-only">
          Resident Access
        </EditableToken>
        <div className="ra-grid">
          {/* Pay Rent */}
          <div className="ra-card">
            <EditableSlotGroup groupPath="residentaccess.payrent_image" definition={payRentImageDef}>
              <CardImage image={payRentImage} label={payRentLabel} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="residentaccess.payrent_link" definition={payRentLinkDef}>
              <CardBar link={payRentLink} label={payRentLabel} bg={standardBg} color={standardText} />
            </EditableSlotGroup>
          </div>

          {/* Resident Portal (highlighted) */}
          <div className="ra-card">
            <EditableSlotGroup groupPath="residentaccess.portal_image" definition={portalImageDef}>
              <CardImage image={portalImage} label={portalLabel} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="residentaccess.portal_link" definition={portalLinkDef}>
              <CardBar link={portalLink} label={portalLabel} bg={highlightBg} color={highlightText} />
            </EditableSlotGroup>
          </div>

          {/* Service Request */}
          <div className="ra-card">
            <EditableSlotGroup groupPath="residentaccess.service_image" definition={serviceImageDef}>
              <CardImage image={serviceImage} label={serviceLabel} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="residentaccess.service_link" definition={serviceLinkDef}>
              <CardBar link={serviceLink} label={serviceLabel} bg={standardBg} color={standardText} />
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .ra-section {
          padding: 3rem 0 4rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ra-section { padding: 4rem 0 5rem; }
        }
        .ra-container {
          max-width: 78rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ra-container { padding: 0 1.5rem; } }

        .ra-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .ra-grid { grid-template-columns: repeat(3, 1fr); }
        }

        .ra-card {
          position: relative;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border-radius: var(--vibe-radius-lg, 12px);
          background: #fff;
          box-shadow: 0 6px 24px rgba(52, 46, 31, 0.08);
          transition: box-shadow 0.25s ease, transform 0.25s ease;
        }
        .ra-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 40px rgba(52, 46, 31, 0.14);
        }

        .ra-card-media {
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          background: var(--vibe-color-surface, #f5f3ef);
        }
        .ra-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.4s ease;
        }
        .ra-card:hover .ra-card-img { transform: scale(1.04); }
        .ra-card-img--placeholder {
          background: var(--vibe-color-surface, #f5f3ef);
        }

        .ra-card-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.35rem 1rem;
          text-decoration: none;
        }
        /* Stretch the bar's anchor over the whole card so the image is clickable
           too, without nesting the image slot group inside the anchor. */
        .ra-card-stretch::after {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 1;
        }
        .ra-card-label {
          font-family: var(--vibe-font-heading);
          font-size: 1.05rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: inherit;
        }
      `}</style>
    </section>
  );
}

(ResidentAccess as any).contentSchema = contentSchema;

export default ResidentAccess;
